import {
  Menu,
  MenuButton,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Overflow as FluentOverflow,
  OverflowItem,
  useIsOverflowItemVisible,
  useOverflowMenu,
  ButtonProps,
  mergeClasses,
} from '@fluentui/react-components';
import { TIcon } from '@shared/types';
import classNames from 'classnames';
import { ControlledTooltip } from './ControlledTooltip';
import { ButtonLoading, MenuItemLoading } from './loading';
import { useMenuButtonStyles } from '@shared/hooks';
import { FC, ReactNode } from 'react';

export type TOverflowButtonParam = {
  children: string;
  icon: TIcon;
  appearance?: ButtonProps['appearance'];
  onClick?: () => void;
  disabled?: boolean;
  disabledMessage?: string;
  value?: string;
};

export type TOverflowNodeParam = {
  node: ReactNode; // render exact JSX button if provided
  hideInMenu?: boolean; // skip rendering in the overflow "More" menu (e.g. dividers)
};

const isTOverflowNodeParam = (param: TOverflowButtonParam | TOverflowNodeParam): param is TOverflowNodeParam =>
  (param as TOverflowNodeParam).node !== undefined;

type TPropsMenuItem = {
  id: string;
  param: TOverflowButtonParam | TOverflowNodeParam;
};

const OverflowMenuItem = ({ id, param }: TPropsMenuItem) => {
  const isVisible = useIsOverflowItemVisible(id);
  if (isVisible) return null;
  if (isTOverflowNodeParam(param) && param.hideInMenu) return null;

  return isTOverflowNodeParam(param) ? (
    param.node
  ) : (
    <ControlledTooltip
      relationship="inaccessible"
      enable={param.disabled}
      content={param.disabledMessage || ''}
      withArrow
      positioning="after"
    >
      <MenuItemLoading {...param} />
    </ControlledTooltip>
  );
};

const OverflowMenu: FC<TProps> = ({ params, label, defaultAppearance, menuButtonClassname }) => {
  const { ref, overflowCount, isOverflowing } = useOverflowMenu<HTMLButtonElement>();
  const classes = useMenuButtonStyles();

  if (!isOverflowing) return null;

  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <MenuButton
          appearance={defaultAppearance}
          ref={ref}
          className={mergeClasses(classes.menuButton, menuButtonClassname)}
        >
          {label || `+${overflowCount} actions`}
        </MenuButton>
      </MenuTrigger>

      <MenuPopover className={classes.menuPopover}>
        <MenuList>
          {params.filter(Boolean).map((param, idx) => (
            <OverflowMenuItem key={idx} id={idx.toString()} param={param} />
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

type TProps = {
  params: (TOverflowButtonParam | TOverflowNodeParam)[];
  label?: string;
  defaultAppearance?: ButtonProps['appearance'];
  menuButtonClassname?: string;
  spacing?: boolean;
  alignment?: 'center' | 'start' | 'end';
};

const Overflow = ({ params, label, defaultAppearance, menuButtonClassname, spacing, alignment = 'start' }: TProps) => {
  return (
    <FluentOverflow>
      <div>
        <div className={`flex items-center justify-${alignment}`}>
          {params.map((param, idx) => (
            <OverflowItem key={idx} id={idx.toString()}>
              <span
                className={classNames('inline-flex items-stretch', { 'pr-2': spacing, 'pl-1.5': spacing && idx === 0 })}
              >
                {isTOverflowNodeParam(param) ? (
                  param.node
                ) : (
                  <ControlledTooltip
                    relationship="description"
                    content={(param.disabled && param.disabledMessage) || ''}
                    withArrow
                  >
                    <ButtonLoading appearance={defaultAppearance} {...param} />
                  </ControlledTooltip>
                )}
              </span>
            </OverflowItem>
          ))}
          <OverflowMenu
            params={params}
            label={label}
            defaultAppearance={defaultAppearance}
            menuButtonClassname={menuButtonClassname}
          />
        </div>
      </div>
    </FluentOverflow>
  );
};

export { Overflow };
